/* Desktop purchase hierarchy: move existing informational nodes, never clone
   app controls/forms. Comment anchors restore the exact mobile DOM order. */
(() => {
  // sections/main-product runs this inline while the page is parsed (no jump after first paint); skip the copy here.
  if (window.lwPurchaseLayout) return;
  const desktop = window.matchMedia('(min-width: 992px)');
  const instances = new Map();
  function mount(list) {
    if (instances.has(list)) return;
    const anchor = list.querySelector(':scope > [id$="-variants-container"], :scope > .bber-sku-row');
    if (!anchor) return;
    const nodes = ['.bber-save-review-shipping-after-sku', '.bber-shipping-note']
      .map(selector => list.querySelector(':scope > ' + selector)).filter(Boolean);
    const entries = nodes.map(node => {
      const marker = document.createComment('lw-purchase-original-position');
      node.before(marker);
      return {node, marker};
    });
    const product = list.closest('.main-product');
    const quantity = product?.querySelector('.main-product_quantity_input-block');
    const sku = list.querySelector(':scope > .bber-sku-row');
    const compactMeta = quantity && sku && anchor !== sku;
    const quantityMarker = compactMeta ? document.createComment('lw-quantity-position') : null;
    const skuMarker = compactMeta ? document.createComment('lw-sku-position') : null;
    const selectionFooter = document.createElement('div');
    selectionFooter.className = 'lw-purchase-selection-footer';
    const metaList = document.createElement('ul');
    metaList.className = 'list-unstyled lw-purchase-meta';
    if (compactMeta) { quantity.before(quantityMarker); sku.before(skuMarker); }
    const sync = () => {
      entries.forEach(({node, marker}) => {
        // Owner request 10-14: "Free Shipping" (and the app's points line after it) sits under the price on every size.
        if (desktop.matches || node.matches('.bber-shipping-note')) list.insertBefore(node, anchor);
        else marker.after(node);
      });
      if (compactMeta) {
        if (desktop.matches) {
          quantityMarker.after(selectionFooter);
          selectionFooter.append(quantity, metaList);
          metaList.append(sku);
        } else {
          quantityMarker.after(quantity);
          skuMarker.after(sku);
          selectionFooter.remove();
        }
      }
      list.toggleAttribute('data-lw-desktop-purchase', desktop.matches);
    };
    desktop.addEventListener('change', sync);
    sync();
    instances.set(list, () => {
      desktop.removeEventListener('change', sync);
      entries.forEach(({node, marker}) => { marker.replaceWith(node); });
      if (compactMeta) {
        quantityMarker.replaceWith(quantity);
        skuMarker.replaceWith(sku);
        selectionFooter.remove();
      }
      list.removeAttribute('data-lw-desktop-purchase');
    });
  }
  const initialise = (scope = document) => scope.querySelectorAll('body.lw-theme .main-product_paver-list').forEach(mount);
  initialise();
  document.addEventListener('shopify:section:load', event => initialise(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((dispose, list) => {
      if (event.target.contains(list)) { dispose(); instances.delete(list); }
    });
  });
})();

(() => {
  if (window.__littleWorldTheme) return;
  window.__littleWorldTheme = true;
  const initialised = new WeakSet();
  let origin = null;
  let stopSearchViewport = null;
  const strings = JSON.parse(document.getElementById('lw-strings')?.textContent || '{}');
  function fitSearchViewport(dialog) {
    stopSearchViewport?.();
    const viewport = window.visualViewport;
    let frame = 0;
    const update = () => {
      frame = 0;
      dialog.style.setProperty('--lw-search-visible-height', `${viewport?.height || window.innerHeight}px`);
      dialog.style.setProperty('--lw-search-visible-top', `${Math.max(0, viewport?.offsetTop || 0)}px`);
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    update();
    viewport?.addEventListener('resize', schedule);
    viewport?.addEventListener('scroll', schedule);
    window.addEventListener('resize', schedule);
    const cleanup = () => {
      cancelAnimationFrame(frame);
      viewport?.removeEventListener('resize', schedule);
      viewport?.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      dialog.removeEventListener('close', cleanup);
      dialog.style.removeProperty('--lw-search-visible-height');
      dialog.style.removeProperty('--lw-search-visible-top');
      if (stopSearchViewport === cleanup) stopSearchViewport = null;
    };
    dialog.addEventListener('close', cleanup);
    stopSearchViewport = cleanup;
  }
  function syncDialogs() {
    document.body.classList.toggle('lw-overlay', !!document.querySelector('dialog.lw-dialog[open]'));
    document.querySelectorAll('[data-lw-open]').forEach(b => b.setAttribute('aria-expanded', String(!!document.getElementById(b.dataset.lwOpen)?.open)));
  }
  function closeDialogs() { document.querySelectorAll('dialog.lw-dialog[open]').forEach(d => d.close()); }
  function openDialog(id, trigger) {
    const dialog = document.getElementById(id);
    if (!dialog) return;
    if (!trigger.closest('dialog.lw-dialog')) origin = trigger;
    closeDialogs();
    if (dialog.classList.contains('lw-search-dialog')) fitSearchViewport(dialog);
    dialog.showModal();
    syncDialogs();
    (dialog.querySelector('[data-lw-query]') || dialog.querySelector('[data-lw-close]'))?.focus({preventScroll:true});
  }
  document.addEventListener('click', event => {
    const shippingLink = event.target.closest('.shipping-subtitle a[href$="/pages/shipping-and-delivery#free-shipping"]');
    if (shippingLink && !event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
      shippingLink.closest('modal-component')?.close();
    }
    const trigger = event.target.closest('[data-lw-open]');
    if (trigger) { event.preventDefault(); openDialog(trigger.dataset.lwOpen,trigger); return; }
    const closer = event.target.closest('[data-lw-close]');
    if (closer) { closer.closest('dialog')?.close(); return; }
    if (event.target.closest('dialog.lw-dialog a[href]')) closeDialogs();
  });
  function initialise(root=document) {
    root.querySelectorAll('dialog.lw-dialog').forEach(dialog => {
      if (initialised.has(dialog)) return;
      initialised.add(dialog);
      dialog.addEventListener('close', () => {
        syncDialogs();
        if (!document.querySelector('dialog.lw-dialog[open]') && origin?.isConnected) origin.focus({preventScroll:true});
      });
      dialog.addEventListener('click', event => {
        if (event.target!==dialog) return;
        const r=dialog.getBoundingClientRect();
        if(event.clientX<r.left||event.clientX>r.right||event.clientY<r.top||event.clientY>r.bottom) dialog.close();
      });
    });
    root.querySelectorAll('[data-lw-search]').forEach(form => {
      if (initialised.has(form)) return;
      initialised.add(form);
      const dialog=form.closest('dialog'),input=form.querySelector('[data-lw-query]'),results=dialog.querySelector('[data-lw-search-results]'),status=dialog.querySelector('[data-lw-search-status]');
      let timer,controller,version=0;
      input.addEventListener('input',()=>{
        clearTimeout(timer);controller?.abort();const current=++version,q=input.value.trim();
        if(!q){results.replaceChildren();status.textContent='';results.removeAttribute('aria-busy');return;}
        timer=setTimeout(async()=>{
          controller=new AbortController();results.setAttribute('aria-busy','true');status.textContent=strings.searchLoading||'';
          try{
            const url=new URL(form.dataset.predictiveUrl,location.origin);
            url.searchParams.set('q',q);url.searchParams.set('resources[type]','product,collection');url.searchParams.set('resources[limit]','5');url.searchParams.set('section_id','lw-predictive-search');
            const response=await fetch(url,{signal:controller.signal,credentials:'same-origin'});
            if(!response.ok)throw new Error('Suggestions unavailable');
            const html=await response.text();if(current!==version)return;
            const fresh=new DOMParser().parseFromString(html,'text/html').getElementById('lw-predictive-results');
            if(!fresh)throw new Error('Suggestions unavailable');
            results.replaceChildren(...fresh.childNodes);status.textContent=strings.searchReady||'';
          }catch(error){if(error.name!=='AbortError'&&current===version){results.replaceChildren();status.textContent=strings.searchError||'';}}
          finally{if(current===version)results.removeAttribute('aria-busy');}
        },200);
      });
      dialog.addEventListener('close',()=>{clearTimeout(timer);controller?.abort();});
    });
  }
  initialise();
  document.addEventListener('shopify:section:load',e=>initialise(e.target));
  document.addEventListener('shopify:section:unload',()=>{stopSearchViewport?.();syncDialogs();});
})();

(() => {
  const mounted = new WeakSet();
  const cleanups = new Map();
  function mountCampaign(root) {
    if (mounted.has(root)) return;
    mounted.add(root);
    const track=root.querySelector('[data-lw-campaign-track]');
    if(!track)return;
    const slides=[...track.querySelectorAll('[data-lw-slide]')],dots=[...root.querySelectorAll('[data-lw-campaign-index]')],count=root.querySelector('[data-lw-campaign-count]'),pause=root.querySelector('[data-lw-pause]'),captions=[...root.querySelectorAll('[data-lw-caption]')];
    const motion=matchMedia('(prefers-reduced-motion: reduce)');
    let active=0,timer=null,userPaused=false,inView=false,hovered=false;
    const setActive=i=>{active=i;dots.forEach((b,n)=>b.setAttribute('aria-pressed',String(i===n)));slides.forEach((s,n)=>{s.inert=n!==i;s.setAttribute('aria-hidden',String(n!==i));});captions.forEach((c,n)=>{c.hidden=n!==i;});track.style.setProperty('--lw-desktop-ratio',slides[i].dataset.desktopRatio||'2.25');track.style.setProperty('--lw-mobile-ratio',slides[i].dataset.mobileRatio||'2.25');if(count)count.textContent=`${i+1} / ${slides.length}`;};
    const stop=()=>{clearTimeout(timer);timer=null;};
    const schedule=()=>{stop();if(root.dataset.autoplay!=='true'||userPaused||hovered||!inView||motion.matches||document.hidden||root.contains(document.activeElement)||slides.length<2)return;timer=setTimeout(()=>{go(active+1);schedule();},Number(root.dataset.interval)||7000);};
    function go(i){i=(i+slides.length)%slides.length;const width=track.clientWidth;setActive(i);track.scrollTo({left:i*width,behavior:motion.matches?'instant':'smooth'});}
    let settleTimer=null;
    const settleSlide=()=>{clearTimeout(settleTimer);const i=Math.max(0,Math.min(slides.length-1,Math.round(track.scrollLeft/track.getBoundingClientRect().width)));setActive(i);};
    const onScroll=()=>{clearTimeout(settleTimer);settleTimer=setTimeout(settleSlide,160);};
    track.addEventListener('scroll',onScroll,{passive:true});track.addEventListener('scrollend',settleSlide);
    let trackWidth=track.clientWidth;const resizeObserver=new ResizeObserver(entries=>{const width=entries[0].contentRect.width;if(Math.abs(width-trackWidth)<1)return;trackWidth=width;track.scrollTo({left:active*width,behavior:'instant'});});resizeObserver.observe(track);
    const viewObserver=new IntersectionObserver(entries=>{inView=entries[0].isIntersecting;schedule();},{threshold:.1});viewObserver.observe(root);
    root.querySelector('[data-lw-prev]')?.addEventListener('click',()=>{go(active-1);schedule();});root.querySelector('[data-lw-next]')?.addEventListener('click',()=>{go(active+1);schedule();});
    dots.forEach((b,i)=>b.addEventListener('click',()=>{go(i);schedule();}));
    track.addEventListener('keydown',e=>{if(e.key==='ArrowRight'||e.key==='ArrowLeft'){e.preventDefault();track.focus({preventScroll:true});go(active+(e.key==='ArrowRight'?1:-1));}if(e.key==='Home'||e.key==='End'){e.preventDefault();go(e.key==='Home'?0:slides.length-1);}});
    pause?.addEventListener('click',()=>{userPaused=!userPaused;pause.setAttribute('aria-pressed',String(userPaused));pause.textContent=userPaused?pause.dataset.playLabel:pause.dataset.pauseLabel;schedule();});
    root.addEventListener('mouseenter',()=>{hovered=true;stop();});root.addEventListener('mouseleave',()=>{hovered=false;schedule();});root.addEventListener('focusin',stop);root.addEventListener('focusout',schedule);track.addEventListener('pointerdown',stop);track.addEventListener('pointerup',schedule);
    document.addEventListener('visibilitychange',schedule);motion.addEventListener('change',schedule);
    const onBlock=e=>{const slide=e.target.closest('[data-lw-slide]');if(slide&&root.contains(slide)){userPaused=true;go(slides.indexOf(slide));stop();}};document.addEventListener('shopify:block:select',onBlock);
    setActive(0);cleanups.set(root,()=>{stop();clearTimeout(settleTimer);track.removeEventListener('scroll',onScroll);track.removeEventListener('scrollend',settleSlide);viewObserver.disconnect();resizeObserver.disconnect();document.removeEventListener('visibilitychange',schedule);motion.removeEventListener('change',schedule);document.removeEventListener('shopify:block:select',onBlock);});
  }
  function init(scope=document){scope.querySelectorAll('[data-lw-campaign]').forEach(mountCampaign);}
  init();document.addEventListener('shopify:section:load',e=>init(e.target));document.addEventListener('shopify:section:unload',e=>{for(const [root,cleanup] of cleanups)if(e.target.contains(root)){cleanup();cleanups.delete(root);}});

  const rewardsStyle=`:host .gw-btn.gw-rd-popup-widget-launcher-btn{position:fixed!important;top:auto!important;left:auto!important;right:20px!important;bottom:var(--lw-rewards-bottom,100px)!important;margin:0!important;transform:none!important;height:48px!important;min-height:48px!important;display:flex!important;align-items:center!important;justify-content:center!important;box-sizing:border-box!important;border-radius:17px!important;padding:0 15px!important;background:linear-gradient(112deg,#c5f3fb9c,#edf6fac9 48%,#f8e4f1b5)!important;color:#143e54!important;border:0!important;box-shadow:-6px 2px 20px #64d7ed26,7px 2px 20px #eda8d127,0 6px 18px #153b511c!important;backdrop-filter:blur(28px) saturate(145%);-webkit-backdrop-filter:blur(28px) saturate(145%)}:host .gw-btn.gw-rd-popup-widget-launcher-btn .gw-layout-text{font-size:12px!important;color:#143e54!important}:host .gw-btn.gw-rd-popup-widget-launcher-btn svg{color:#143e54!important;width:20px!important;height:20px!important}:host .gw-btn.gw-rd-popup-widget-launcher-btn:focus-visible{outline:3px solid #087da7!important;outline-offset:4px!important}@media(max-width:767px){:host .gw-btn.gw-rd-popup-widget-launcher-btn{width:48px!important;min-width:48px!important;padding:0!important}:host .gw-btn.gw-rd-popup-widget-launcher-btn .gw-layout-text{display:none!important}}@media(prefers-reduced-transparency:reduce),(prefers-contrast:more){:host .gw-btn.gw-rd-popup-widget-launcher-btn{background:#edf5f8!important;backdrop-filter:none!important;-webkit-backdrop-filter:none!important;border-color:#143e54!important}}`;
  const rewardsIconStyle=`:host .gw-btn.gw-rd-popup-widget-launcher-btn .gw-rd-popup-widget-launcher-btn__content-icon{transform:none!important;display:grid!important;place-items:center!important}:host .gw-btn.gw-rd-popup-widget-launcher-btn .gw-rd-dynamic-customer-icon{display:grid!important;place-items:center!important}`;
  const watchedRoots=new WeakSet(),watchedHosts=new WeakSet(),watchedButtons=new WeakSet();
  let floatingFrame=null;
  function scheduleFloating(){if(floatingFrame===null)floatingFrame=requestAnimationFrame(syncFloating);}
  // Keep mobile purchase controls clear without hiding any app dialog.
  const visiblePurchaseBlocks=new Set(),launcherVisibility=new WeakMap(),installmentBindings=new Map();
  function watchInstallments(block){
    if(installmentBindings.has(block))return;
    const sync=()=>{
      const variant=block.querySelector('.installment input[name="id"]')?.value;
      const terms=block.querySelector('shopify-payment-terms');
      if(variant&&terms&&terms.getAttribute('variant-id')!==variant)terms.setAttribute('variant-id',variant);
    };
    const observer=new MutationObserver(sync);
    observer.observe(block,{childList:true,subtree:true,attributes:true,attributeFilter:['value']});
    block.addEventListener('change',sync);
    installmentBindings.set(block,{sync,dispose:()=>{observer.disconnect();block.removeEventListener('change',sync);}});
    sync();
  }
  const purchaseObserver=new IntersectionObserver(entries=>{
    entries.forEach(entry=>{if(entry.isIntersecting)visiblePurchaseBlocks.add(entry.target);else visiblePurchaseBlocks.delete(entry.target);});
    scheduleFloating();
  },{rootMargin:'-90px 0px 0px',threshold:0});
  function watchPurchase(scope=document){
    if(document.body.dataset.lwPage!=='product')return;
    scope.querySelectorAll('.main-product_quantity_input-block,.main-product_buttons-block,.bber-share-registry-row').forEach(block=>{purchaseObserver.observe(block);if(block.classList.contains('main-product_buttons-block'))watchInstallments(block);});
  }
  function setLauncherObscured(launcher,obscured){
    if(!launcher)return;
    const active=launcher.getRootNode().activeElement;
    if(obscured&&(launcher===active||launcher.contains(active)))return;
    if(obscured&&!launcherVisibility.has(launcher)){
      launcherVisibility.set(launcher,{value:launcher.style.getPropertyValue('visibility'),priority:launcher.style.getPropertyPriority('visibility')});
      launcher.style.setProperty('visibility','hidden','important');
    }else if(!obscured&&launcherVisibility.has(launcher)){
      const previous=launcherVisibility.get(launcher);
      if(previous.value)launcher.style.setProperty('visibility',previous.value,previous.priority);else launcher.style.removeProperty('visibility');
      launcherVisibility.delete(launcher);
    }
  }
  watchPurchase();
  document.addEventListener('change',e=>{if(e.target.closest('.main-product'))requestAnimationFrame(()=>installmentBindings.forEach(binding=>binding.sync()));});
  document.addEventListener('shopify:section:load',e=>watchPurchase(e.target));
  document.addEventListener('shopify:section:unload',e=>{visiblePurchaseBlocks.forEach(block=>{if(e.target.contains(block)){purchaseObserver.unobserve(block);visiblePurchaseBlocks.delete(block);}});installmentBindings.forEach((binding,block)=>{if(e.target.contains(block)){binding.dispose();installmentBindings.delete(block);}});scheduleFloating();});
  const floatingResize=new ResizeObserver(scheduleFloating);
  function watchRoot(root){
    if(watchedRoots.has(root))return;
    watchedRoots.add(root);
    new MutationObserver(scheduleFloating).observe(root,{childList:true,subtree:true,attributes:true,attributeFilter:['class','style','aria-expanded','aria-label']});
  }
  function syncFloating(){
    floatingFrame=null;
    const rewardsHost=document.querySelector('.gw-rd-popup-widget-placeholder');
    const rewardsRoot=rewardsHost?.shadowRoot;
    const chatHost=document.querySelector('shopify-chat');
    const chatRoot=chatHost?.shadowRoot;
    const chatRuntime=chatRoot?.querySelector('shopify-agent-runtime');
    const chatRuntimeRoot=chatRuntime?.shadowRoot;
    [rewardsHost,chatHost,chatRuntime].forEach(host=>{if(host&&!watchedHosts.has(host)){watchedHosts.add(host);new MutationObserver(scheduleFloating).observe(host,{childList:true});[500,1500,3000].forEach(delay=>setTimeout(scheduleFloating,delay));}});
    [rewardsRoot,chatRoot,chatRuntimeRoot].filter(Boolean).forEach(watchRoot);
    const rewards=rewardsRoot?.querySelector('.gw-rd-popup-widget-launcher-btn');
    const chat=chatRuntimeRoot?.querySelector('button[part="activator"]')||chatRoot?.querySelector('button[part="activator"]');
    [rewards,chat].filter(Boolean).forEach(button=>{if(!watchedButtons.has(button)){watchedButtons.add(button);floatingResize.observe(button);}});
    const viewport=document.documentElement.clientHeight;
    const chatRect=chat?.getBoundingClientRect();
    const chatVisible=chatRect&&chatRect.width>0&&chatRect.height>0&&chatRect.top<viewport&&chatRect.bottom>0;
    const bottom=chatVisible?Math.max(84,Math.ceil(viewport-chatRect.top+16)):100;
    const topBottom=bottom+64;
    const rootStyle=document.documentElement.style;
    if(rootStyle.getPropertyValue('--lw-rewards-bottom')!==`${bottom}px`)rootStyle.setProperty('--lw-rewards-bottom',`${bottom}px`);
    if(rootStyle.getPropertyValue('--lw-top-bottom')!==`${topBottom}px`)rootStyle.setProperty('--lw-top-bottom',`${topBottom}px`);
    if(rewardsRoot&&!rewardsRoot.querySelector('[data-lw-rewards-style]')){const style=document.createElement('style');style.dataset.lwRewardsStyle='true';style.textContent=rewardsStyle+rewardsIconStyle;rewardsRoot.appendChild(style);}
    const obscureLaunchers=window.matchMedia('(max-width:767px)').matches&&visiblePurchaseBlocks.size>0;
    [rewards,chat,document.querySelector('.gw-widget-placeholder-RC-wrapper'),document.querySelector('#back-to-top')].forEach(launcher=>setLauncherObscured(launcher,obscureLaunchers));
  }
  const floatingMountObserver=new MutationObserver(records=>{if(records.some(record=>[...record.addedNodes].some(node=>node.nodeType===1&&node.tagName!=='STYLE')))scheduleFloating();});
  floatingMountObserver.observe(document.body,{childList:true,subtree:true});
  window.addEventListener('resize',scheduleFloating,{passive:true});
  window.visualViewport?.addEventListener('resize',scheduleFloating,{passive:true});
  document.addEventListener('pointerdown',scheduleFloating,{passive:true});
  document.addEventListener('keydown',scheduleFloating,{passive:true});
  document.addEventListener('visibilitychange',scheduleFloating);
  syncFloating();
})();

(() => {
  const instances = new Map();
  function mount(root) {
    if (instances.has(root)) return;
    const track = root.querySelector('[data-lw-arrivals-track]');
    const cards = [...track.querySelectorAll('.product-column')];
    const controls = root.querySelector('[data-lw-arrivals-controls]');
    const previous = root.querySelector('[data-lw-arrivals-prev]');
    const next = root.querySelector('[data-lw-arrivals-next]');
    const position = root.querySelector('[data-lw-arrivals-position]');
    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    const mobile = matchMedia('(max-width: 600px)');
    let timer;
    function measure() {
      const box = track.getBoundingClientRect();
      const widths = cards.map(card => card.getBoundingClientRect());
      const visible = widths.map((r, i) => ({i, fraction: Math.max(0, Math.min(r.right, box.right) - Math.max(r.left, box.left)) / r.width})).filter(r => r.fraction >= .5);
      const nextColumn = mobile.matches ? 4 : 1;
      return {max: track.scrollWidth - track.clientWidth, left: track.scrollLeft, step: widths.length > nextColumn ? widths[nextColumn].left - widths[0].left : box.width, width: track.clientWidth, first: visible[0]?.i || 0, last: visible.at(-1)?.i || 0};
    }
    function update() {
      clearTimeout(timer);
      const state = measure();
      controls.hidden = state.max <= 2 || cards.length < 2;
      previous.setAttribute('aria-disabled', String(state.left <= 2));
      next.setAttribute('aria-disabled', String(state.left >= state.max - 2));
      const range = state.first === state.last ? `${state.first + 1}` : `${state.first + 1}–${state.last + 1}`;
      const label = `${range} / ${cards.length}`;
      if (position.textContent !== label) position.textContent = label;
    }
    function go(direction) {
      const state = measure();
      const distance = mobile.matches ? state.step : Math.max(1, Math.floor(state.width / state.step)) * state.step;
      track.scrollTo({left: Math.max(0, Math.min(state.max, state.left + direction * distance)), behavior: motion.matches ? 'instant' : 'smooth'});
    }
    const onPrevious = () => { if (previous.getAttribute('aria-disabled') !== 'true') go(-1); };
    const onNext = () => { if (next.getAttribute('aria-disabled') !== 'true') go(1); };
    const onScroll = () => { clearTimeout(timer); timer = setTimeout(update, 120); };
    const onKey = event => {
      if (event.target !== track || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); go(event.key === 'ArrowRight' ? 1 : -1); }
      if (event.key === 'Home' || event.key === 'End') { event.preventDefault(); track.scrollTo({left: event.key === 'Home' ? 0 : track.scrollWidth, behavior: motion.matches ? 'instant' : 'smooth'}); }
    };
    previous.addEventListener('click', onPrevious);
    next.addEventListener('click', onNext);
    track.addEventListener('scroll', onScroll, {passive:true});
    track.addEventListener('scrollend', update);
    track.addEventListener('keydown', onKey);
    const resize = new ResizeObserver(update);
    resize.observe(track);
    update();
    instances.set(root, () => {
      clearTimeout(timer); resize.disconnect();
      previous.removeEventListener('click', onPrevious); next.removeEventListener('click', onNext);
      track.removeEventListener('scroll', onScroll); track.removeEventListener('scrollend', update); track.removeEventListener('keydown', onKey);
    });
  }
  const initialise = (root = document) => root.querySelectorAll('[data-lw-arrivals]').forEach(mount);
  initialise();
  document.addEventListener('shopify:section:load', event => initialise(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((cleanup, root) => { if (event.target.contains(root)) { cleanup(); instances.delete(root); } });
  });
})();


(() => {
  function rewardsMessage(earned, balance) {
    const n = value => value.toLocaleString('en-CA', {maximumFractionDigits: 2});
    const total = earned + (balance === null ? 0 : balance);
    const credit = Math.floor(total / 1000) * 60 + Math.floor((total % 1000) / 200) * 10;
    if (credit > 0) {
      return balance === null
        ? `Earn ${n(earned)} points. Redeem for C$${n(credit)} store credit.`
        : `Earn ${n(earned)} points. Unlock C$${n(credit)} total store credit.`;
    }
    if (balance === null) return `Earn ${n(earned)} points. 200 points = C$10 store credit.`;
    return `Earn ${n(earned)} points. Just ${n(Math.ceil(200 - total))} more for C$10 store credit.`;
  }
  const context = document.querySelector('[data-lw-rewards-context]');
  if (!context || context.dataset.currency !== 'CAD' || !/^en(?:-|$)/i.test(document.documentElement.lang)) return;
  const hosts = new WeakSet();
  const widgetResize = new ResizeObserver(() => schedule());
  const roots = new WeakSet();
  let scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; update(); });
  }
  function observe(root) {
    if (!root || roots.has(root)) return;
    roots.add(root);
    new MutationObserver(schedule).observe(root, {childList: true, subtree: true, characterData: true});
  }
  function balanceValue() {
    if (context.dataset.loggedIn !== 'true') return null;
    const host = context.querySelector('.gw-rd-customer-points-balance-inline-widget-placeholder');
    const root = host?.shadowRoot;
    observe(root);
    if (!root) return null;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let raw = '', node;
    while ((node = walker.nextNode())) { if (!node.parentElement?.closest('style,script')) raw += node.textContent; }
    const text = raw.trim().replace(/,/g, '');
    return /^\d+(?:\.\d+)?$/.test(text) ? Number(text) : null;
  }
  function update() {
    const balance = balanceValue();
    document.querySelectorAll('.main-product .gw-rd-earn-points-product-page-widget-placeholder').forEach(host => {
      if (!hosts.has(host)) { hosts.add(host); observe(host); widgetResize.observe(host); }
      const root = host.shadowRoot;
      observe(root);
      if (!root) return;
      if (!root.querySelector('[data-lw-rewards-empty-style]')) {
        const emptyStyle = document.createElement('style');
        emptyStyle.dataset.lwRewardsEmptyStyle = '';
        emptyStyle.textContent = '.gw-rd-earn-points-product-page-widget:not(:has(.gw-rd-earn-points-product-page-widget__points-purchase)){padding:0!important;min-height:0!important}.gw-rd-earn-points-product-page-widget .base-skeleton{display:none!important}';
        root.appendChild(emptyStyle);
      }
      const row = root.querySelector('.gw-rd-earn-points-product-page-widget__points-purchase');
      host.toggleAttribute('data-lw-rewards-ready', !!row);
      if (!row) return;
      const source = [...row.children].find(e => !e.hasAttribute('data-lw-rewards-copy'));
      const match = source?.textContent.trim().match(/^Earn\s+([\d,]+(?:\.\d+)?)\s+points?\s+on this purchase\.$/i);
      let copy = row.querySelector('[data-lw-rewards-copy]');
      const earned = match ? Number(match[1].replace(/,/g, '')) : NaN;
      if (!Number.isFinite(earned) || earned <= 0) {
        copy?.remove();
        row.querySelector('[data-lw-rewards-terms]')?.remove();
        source?.removeAttribute('data-lw-rewards-source');
        return;
      }
      if (!copy) {
        copy = document.createElement('span');
        copy.dataset.lwRewardsCopy = '';
        row.appendChild(copy);
      }
      source.setAttribute('data-lw-rewards-source', '');
      const text = rewardsMessage(earned, balance);
      if (copy.textContent !== text) copy.textContent = text;
      if (!root.querySelector('[data-lw-rewards-style]')) {
        const style = document.createElement('style');
        style.dataset.lwRewardsStyle = '';
        style.textContent = '[data-lw-rewards-source]{display:none!important}.gw-rd-earn-points-product-page-widget{padding:12px 0 14px!important}.gw-rd-earn-points-product-page-widget__earn-points{display:grid!important;grid-template-columns:minmax(0,1fr) auto;align-items:start!important;gap:12px!important;width:100%;line-height:20px!important}.gw-rd-earn-points-product-page-widget__points-purchase{display:grid!important;gap:4px;padding:0!important;margin:0!important;min-width:0;line-height:20px!important}[data-lw-rewards-copy]{display:block;font-size:13px;line-height:20px;color:inherit}.gw-rd-earn-points-product-page-widget__earn-points>button{align-self:start;white-space:nowrap;padding:0!important;margin:0!important;min-height:20px;line-height:20px!important}.gw-rd-earn-points-product-page-widget__earn-points>button .gw-layout-text{font-size:13px!important;line-height:20px!important}[data-lw-rewards-terms]{display:block;font-size:11px;line-height:16px;opacity:.8;margin:0}@media(max-width:767px){.gw-rd-earn-points-product-page-widget{padding:10px 0 12px!important}.gw-rd-earn-points-product-page-widget__earn-points{gap:10px!important}[data-lw-rewards-copy],.gw-rd-earn-points-product-page-widget__earn-points>button .gw-layout-text{font-size:12px!important}}';
        root.appendChild(style);
      }
      let terms = row.querySelector('[data-lw-rewards-terms]');
      if (!terms) {
        terms = document.createElement('small');
        terms.dataset.lwRewardsTerms = '';
        terms.textContent = 'Redeem after points are credited. Reward terms apply.';
        copy.after(terms);
      }
    });
  }
  observe(context);
  observe(document.querySelector('.main-product .products_info'));
  document.addEventListener('gwSdkReady', schedule);
  document.addEventListener('shopify:section:load', schedule);
  [0,500,1500,3000,6000,10000].forEach(delay => setTimeout(schedule, delay));
  const mountTimer = setInterval(() => {
    schedule();
    const mounts = [...document.querySelectorAll('.main-product .gw-rd-earn-points-product-page-widget-placeholder')];
    if (mounts.length && mounts.every(host => host.shadowRoot)) clearInterval(mountTimer);
  }, 250);
  setTimeout(() => {
    clearInterval(mountTimer);
    document.querySelectorAll('.main-product .gw-rd-earn-points-product-page-widget-placeholder').forEach(host => {
      if (!host.shadowRoot) host.setAttribute('data-lw-rewards-ready', '');
    });
  }, 45000);
  addEventListener('pagehide', () => clearInterval(mountTimer), {once:true});
})();

// BER Master App → 颜色色块 (Color swatches): card dots and dropdowns (snippets/ber-swatch-card.liquid) and the product page
// dropdown and "Color: value" text (snippets/product-variant-options.liquid).
(function () {
  if (window.__berSwatches) return;
  window.__berSwatches = true;

  // Card: show the chosen variant's photo, price and link.
  function show(el, d) {
    var card = el.closest('product-card') || el.closest('[data-bsw-card]');
    if (!card) return;
    if (d.img) {
      var img = card.querySelector('.product_media .product_gallery-item img');
      if (img) { img.removeAttribute('srcset'); img.src = d.img; }
    }
    if (d.url) {
      card.querySelectorAll('a[href*="/products/"]').forEach(function (a) {
        if (!a.classList.contains('bsw-more')) a.setAttribute('href', d.url);
      });
    }
    var wrap = card.querySelector('.product_price-wrap');
    if (wrap && d.price) {
      var main = wrap.querySelector('.main-price');
      if (main) main.textContent = d.price;
      var was = wrap.querySelector('s.regular-price');
      var badge = wrap.querySelector('.bber-sale-badge');
      if (was) { if (d.compare) was.textContent = d.compare; was.style.display = d.compare ? '' : 'none'; }
      if (badge) badge.style.display = d.compare ? '' : 'none';
    }
  }

  document.addEventListener('click', function (e) {
    var dot = e.target.closest && e.target.closest('.bsw-dot, .bsw-chip');
    if (!dot) return;
    e.preventDefault();
    dot.parentNode.querySelectorAll('.bsw-dot, .bsw-chip').forEach(function (x) {
      x.classList.toggle('is-active', x === dot);
      x.setAttribute('aria-pressed', x === dot ? 'true' : 'false');
    });
    show(dot, dot.dataset);
  });

  // Product page dropdown: check the matching (hidden) radio and let the theme handle that change. Caught before the
  // theme's own listeners see the select, which would not know it.
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t.matches || !t.matches('.bsw-pselect')) return;
    e.stopPropagation();
    var holder = t.closest('.variant-options-holder') || t.parentNode.parentNode;
    var radios = holder.querySelectorAll('input[type="radio"]');
    for (var i = 0; i < radios.length; i++) {
      if (radios[i].value === t.value) {
        radios[i].checked = true;
        radios[i].dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }, true);

  // Product page: a value whose combination with the other choices doesn't exist or is sold out (e.g. a colour that only
  // comes in L/XL while S/M is chosen) moves the other options to the closest variant that can be bought, instead of
  // showing "Unavailable". The theme keeps the choices in data-current-set, so that is set here before it reads it.
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t.matches || !t.checked || !t.matches('.main-product variant-pill input[type="radio"]')) return;
    var pill = t.closest('variant-pill');
    var box = pill && document.querySelector(pill.dataset.parent);
    var json = pill && document.querySelector(pill.dataset.jsonSelector);
    if (!box || !json) return;
    var variants, chosen;
    try { variants = JSON.parse(json.textContent).variants; chosen = JSON.parse(pill.dataset.currentSet); } catch (x) { return; }
    var idx = parseInt(pill.dataset.optionIndex, 10);
    chosen[idx] = t.value;
    var same = function (v) { return v.options.every(function (o, i) { return o === chosen[i]; }); };
    if (variants.some(function (v) { return v.available && same(v); })) return;
    // Closest buyable variant with this value: keeps the most other choices, earlier options first.
    var best = null, bestScore = -1;
    variants.forEach(function (v) {
      if (!v.available || v.options[idx] !== t.value) return;
      var score = 0;
      v.options.forEach(function (o, i) { if (i !== idx && o === chosen[i]) score += 1 << (8 - i); });
      if (score > bestScore) { best = v; bestScore = score; }
    });
    if (!best) return;
    var pills = box.querySelectorAll('variant-pill');
    pills.forEach(function (p) {
      p.setAttribute('data-current-set', JSON.stringify(best.options));
      var j = parseInt(p.dataset.optionIndex, 10);
      if (j === idx || best.options[j] === chosen[j]) return;
      p.querySelectorAll('input[type="radio"]').forEach(function (r) { if (r.value === best.options[j]) r.checked = true; });
      var row = p.closest('.variant-row--pills');
      var label = row && row.querySelector('.lw-native-selected');
      if (label) label.textContent = best.options[j];
      var sel = row && row.querySelector('.bsw-pselect');
      if (sel) sel.value = best.options[j];
    });
  }, true);

  // Card: each row of dots or size boxes stays on one line, so the rows line up across a grid row. What doesn't fit is
  // folded into the "+N" link (never the chosen value). Rows are clipped by CSS until this runs, so nothing moves.
  function fit(row) {
    var items = Array.prototype.slice.call(row.querySelectorAll('.bsw-dot, .bsw-chip'));
    if (!items.length) return;
    var more = row.querySelector('.bsw-more');
    if (more && !more.dataset.base) more.dataset.base = String(parseInt(more.textContent.replace(/\D/g, ''), 10) || 0);
    if (more && more.dataset.added && more.dataset.base === '0') { more.remove(); more = null; }
    items.forEach(function (x) { x.style.display = ''; });
    if (more) more.textContent = '+' + more.dataset.base;
    var cs = getComputedStyle(row);
    var edge = row.getBoundingClientRect().right - parseFloat(cs.paddingRight) + 0.5;
    var last = function () { return more || items.filter(function (x) { return x.style.display !== 'none'; }).pop(); };
    var hidden = 0;
    for (var k = items.length - 1; k > 0 && last().getBoundingClientRect().right > edge; k--) {
      if (items[k].classList.contains('is-active')) continue;
      items[k].style.display = 'none';
      hidden += 1;
      if (!more) {
        more = document.createElement('a');
        more.className = 'bsw-more';
        more.href = (items[0].dataset.url || '').split('?')[0];
        more.dataset.base = '0';
        more.dataset.added = '1';
        row.appendChild(more);
      }
      more.textContent = '+' + (parseInt(more.dataset.base, 10) + hidden);
      more.setAttribute('aria-label', (parseInt(more.dataset.base, 10) + hidden) + ' more');
    }
  }
  var ro = window.ResizeObserver && new ResizeObserver(function (list) { list.forEach(function (en) { fit(en.target); }); });
  var queued = false;
  function scan() {
    queued = false;
    document.querySelectorAll('.bsw-card .bsw-row--img:not([data-bsw-fit]), .bsw-card .bsw-row--chips:not([data-bsw-fit])').forEach(function (row) {
      row.setAttribute('data-bsw-fit', '');
      if (ro) ro.observe(row); else fit(row);
    });
  }
  function queue() { if (!queued) { queued = true; requestAnimationFrame(scan); } }
  queue();
  // Cards added later (filters, load more, quick search) get the same treatment.
  new MutationObserver(queue).observe(document.documentElement, { childList: true, subtree: true });

  // Product page: "Color: <name>" keeps the height of its longest name, so a name that wraps to another line doesn't
  // push the swatches down when it is chosen (phones). Measured with each name in turn, before the next paint.
  function reserve(row) {
    var label = row.querySelector('.lw-native-selected');
    var title = label && label.parentElement;
    if (!title) return;
    var now = label.textContent, max = 0;
    title.style.minHeight = '';
    row.querySelectorAll('input[type="radio"]').forEach(function (r) {
      label.textContent = r.value;
      max = Math.max(max, title.getBoundingClientRect().height);
    });
    label.textContent = now;
    if (max) title.style.minHeight = Math.ceil(max) + 'px';
  }
  var titleRo = window.ResizeObserver && new ResizeObserver(function (list) { list.forEach(function (en) { reserve(en.target); }); });
  document.querySelectorAll('.main-product .variant-row--pills').forEach(function (row) {
    if (!row.querySelector('.lw-native-selected')) return;
    if (titleRo) titleRo.observe(row); else reserve(row);
  });

  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t.matches) return;
    if (t.matches('.bsw-select')) { show(t, t.options[t.selectedIndex].dataset); return; }
    // Product page: "Color: Walrus" follows the chosen value.
    if (t.matches('.main-product variant-pill input[type="radio"]') && t.checked) {
      var row = t.closest('.variant-row--pills');
      var label = row && row.querySelector('.lw-native-selected');
      if (label) label.textContent = t.value;
      var sel = row && row.querySelector('.bsw-pselect');
      if (sel && sel.value !== t.value) sel.value = t.value;
    }
  });
})();

// BER Master App → 颜色色块: magnifying picture swatches (Boss 10-28, "Magnification dock"). The swatch under the pointer
// grows most and its neighbours a little, by distance, spreading apart so none overlap; the colour's name shows above it.
// Phones: a tap grows only that swatch, in place, for a moment and names it (the tap still chooses the colour); the
// others stay still (Boss 10-28). Transforms only, so nothing else on the page moves; with reduced motion only the name
// shows. Styles: .bsw-dock / .bsw-tip in snippets/ber-swatch-card.liquid.
(function () {
  if (window.__berSwatchDock) return;
  window.__berSwatchDock = true;
  var ITEM = '.bsw-dot, .bsw-more, label.bsw-img';
  var reduce = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)');
  var tip = null, cur = null, items = [], bounds = [0, 0], px = 0, py = 0, amp = 0.4, raf = 0, offTimer = 0, tapTimer = 0, tapEl = null, lastType = '';

  function rowOf(el) {
    var row = el && el.closest && el.closest('.bsw-row--img, .variant-options-holder');
    return row && row.querySelector('.bsw-dot, label.bsw-img') ? row : null;
  }
  function itemOf(el) {
    if (!el || !el.closest) return null;
    if (el.matches && el.matches('input') && el.nextElementSibling && el.nextElementSibling.matches('label.bsw-img')) return el.nextElementSibling;
    var it = el.closest(ITEM);
    return it && rowOf(it) ? it : null;
  }
  // Name shown above a swatch: its title on cards (moved here so the browser's own tooltip doesn't show too), the
  // value on the product page, with "Sold out" when that colour can't be bought.
  function tipText(el) {
    if (el.classList.contains('bsw-more')) return '';
    if (el.hasAttribute('title')) { el.setAttribute('data-bsw-tip', el.getAttribute('title')); el.removeAttribute('title'); }
    if (el.hasAttribute('data-bsw-tip')) return el.getAttribute('data-bsw-tip');
    var name = el.getAttribute('data-option-name') || '';
    var input = el.previousElementSibling;
    return input && input.classList.contains('disabled') && el.dataset.bswSo ? name + ' – ' + el.dataset.bswSo : name;
  }

  // Positions are measured once per visit, at rest, relative to the row, so scrolling while hovering stays right.
  function activate(row) {
    clearTimeout(offTimer);
    if (cur && cur !== row) release(true);
    cur = row;
    row.classList.add('bsw-dock');
    var r = row.getBoundingClientRect(), cs = getComputedStyle(row);
    // Card swatches stay inside the card where there is room; product page ones only inside the screen, so the one
    // under the pointer stays under it.
    bounds = row.classList.contains('bsw-row--img')
      ? [parseFloat(cs.paddingLeft) || 0, r.width - (parseFloat(cs.paddingRight) || 0)]
      : [4 - r.left, innerWidth - 4 - r.left];
    // A swatch may still be mid-transition from the last time (pointer left and came back): its box on screen is
    // scaled and shifted, and switching the transform off doesn't show the resting box until the transition ends. So
    // take the resting box from the current transform instead: the size is offsetWidth / offsetHeight, and scaling
    // about the bottom centre leaves that point where it was plus the transform's own shift.
    items = Array.prototype.slice.call(row.querySelectorAll(ITEM)).filter(function (el) { return el.offsetParent; }).map(function (el) {
      var b = el.getBoundingClientRect(), t = getComputedStyle(el).transform, e = 0, f = 0;
      if (t && t !== 'none' && window.DOMMatrixReadOnly) { var m = new DOMMatrixReadOnly(t); e = m.e; f = m.f; }
      var w = el.offsetWidth, h = el.offsetHeight, l = (b.left + b.right) / 2 - e - w / 2 - r.left, bot = b.bottom - f - r.top;
      return { el: el, l: l, w: w, top: bot - h, bot: bot };
    });
    items.sort(function (a, b) { return a.top - b.top || a.l - b.l; });
  }
  function release(now) {
    var row = cur, list = items;
    cur = null; items = []; tapEl = null;
    // raf back to 0 too: queue() only asks for a frame when raf is 0, so a cancelled id left here stopped the dock for
    // good (phones: the 2nd tap moves focus between the radios, focusout released mid-frame, later taps did nothing).
    cancelAnimationFrame(raf); raf = 0;
    if (tip) tip.classList.remove('is-on');
    if (!row) return;
    list.forEach(function (it) { it.el.style.transform = ''; it.el.style.zIndex = ''; });
    // Keep the row unclipped until the swatches have shrunk back.
    if (now) row.classList.remove('bsw-dock');
    else offTimer = setTimeout(function () { row.classList.remove('bsw-dock'); }, 220);
  }
  function queue() { if (!raf) raf = requestAnimationFrame(frame); }
  function frame() {
    raf = 0;
    if (!cur || !cur.isConnected) { release(true); return; }
    // The theme can redraw the picker (e.g. after a variant change): measure the new swatches.
    if (items.some(function (it) { return !it.el.isConnected; })) activate(cur);
    // After a tap, follow the tapped swatch (the colour name above it can wrap and push the row down) and keep the name
    // pinned to it until the tap ends.
    if (tapEl) { pointAt(tapEl); queue(); }
    var r = cur.getBoundingClientRect(), x = px - r.left, y = py - r.top;
    // The line of swatches the pointer is on (product page rows can wrap); the others stay as they are.
    var best = Infinity, line = -1;
    items.forEach(function (it) {
      var d = y < it.top ? it.top - y : y > it.bot ? y - it.bot : 0;
      if (d < best - 1) { best = d; line = it.top; }
    });
    var a = reduce && reduce.matches ? 0 : amp, grow = 0, anchor = 0, near = null, nearD = Infinity;
    items.forEach(function (it) {
      var c = it.l + it.w / 2, d = x - c;
      it.on = Math.abs(it.top - line) < 2;
      if (tapEl) {
        it.s = it.el === tapEl ? 1 + a : 1;
        it.tx = 0;
        it.on = false;
        if (it.el === tapEl) { near = it; nearD = 0; }
        return;
      }
      it.s = it.on ? 1 + a * Math.exp(-(d * d) / (2 * it.w * it.w)) : 1;
      it.tx = 0;
      if (!it.on) return;
      var extra = (it.s - 1) * it.w;
      it.tx = grow + extra / 2;
      // Anchor at the pointer: the swatch under it doesn't slide, those left of it move left, those right move right.
      anchor += extra * Math.min(1, Math.max(0, (x - it.l) / it.w));
      grow += extra;
      if (Math.abs(d) < nearD) { nearD = Math.abs(d); near = it; }
    });
    var lo = Infinity, hi = -Infinity, nudge = 0;
    items.forEach(function (it) {
      if (!it.on) return;
      it.tx -= anchor;
      lo = Math.min(lo, it.l + it.tx - (it.s - 1) * it.w / 2);
      hi = Math.max(hi, it.l + it.w + it.tx + (it.s - 1) * it.w / 2);
    });
    if (hi > bounds[1] + 2) nudge = bounds[1] + 2 - hi;
    if (lo + nudge < bounds[0] - 2) nudge = bounds[0] - 2 - lo;
    items.forEach(function (it) {
      var tx = it.on ? it.tx + nudge : 0, on = it.s > 1.001 || Math.abs(tx) > 0.2;
      it.el.style.transform = on ? 'translateX(' + tx.toFixed(1) + 'px) scale(' + it.s.toFixed(3) + ')' : '';
      it.el.style.zIndex = on ? String(2 + Math.round((it.s - 1) * 20)) : '';
      it.vx = r.left + it.l + it.w / 2 + tx;
      it.vy = r.top + it.bot - (it.bot - it.top) * it.s;
    });
    var text = near && nearD <= near.w / 2 + 6 ? tipText(near.el) : '';
    if (!text) { if (tip) tip.classList.remove('is-on'); return; }
    if (!tip) {
      tip = document.createElement('div');
      tip.className = 'bsw-tip';
      tip.setAttribute('aria-hidden', 'true');
      tip.appendChild(document.createElement('span'));
      document.body.appendChild(tip);
    }
    if (tip.firstChild.textContent !== text) tip.firstChild.textContent = text;
    // Kept inside the screen; the arrow still points at the swatch.
    var half = tip.offsetWidth / 2, left = Math.min(Math.max(near.vx, half + 8), innerWidth - half - 8);
    tip.style.left = left + 'px';
    tip.style.top = (near.vy - 8) + 'px';
    tip.style.setProperty('--ax', (near.vx - left).toFixed(1) + 'px');
    tip.classList.add('is-on');
  }
  function pointAt(el) {
    var r = cur.getBoundingClientRect();
    items.forEach(function (it) {
      if (it.el === el) { px = r.left + it.l + it.w / 2; py = r.top + (it.top + it.bot) / 2; }
    });
  }

  document.addEventListener('pointerover', function (e) {
    if (e.pointerType === 'touch') return;
    var row = rowOf(e.target);
    if (row) { if (row !== cur) { amp = 0.4; activate(row); } px = e.clientX; py = e.clientY; queue(); }
    else if (cur && !tapTimer) release(false);
  }, { passive: true });
  document.addEventListener('pointermove', function (e) {
    if (!cur || e.pointerType === 'touch') return;
    px = e.clientX; py = e.clientY; queue();
  }, { passive: true });
  document.addEventListener('pointerout', function (e) { if (cur && !e.relatedTarget && e.pointerType !== 'touch') release(false); }, { passive: true });
  addEventListener('scroll', function () { if (cur) queue(); }, { passive: true });

  // Phones and tablets: a tap (not a scroll) magnifies that swatch and names it for a moment.
  document.addEventListener('pointerdown', function (e) { lastType = e.pointerType; }, { passive: true, capture: true });
  document.addEventListener('click', function (e) {
    if (lastType !== 'touch') return;
    var it = itemOf(e.target);
    if (!it) return;
    clearTimeout(tapTimer);
    // 46px swatches 6px apart: 1.22 grows ~5px a side, so it never covers its neighbours.
    amp = 0.22;
    activate(rowOf(it));
    tapEl = it;
    pointAt(it);
    queue();
    tapTimer = setTimeout(function () { tapTimer = 0; release(false); }, 1400);
  }, true);

  // Keyboard: the focused swatch is magnified and named the same way.
  document.addEventListener('focusin', function (e) {
    var it = itemOf(e.target);
    if (!it || !e.target.matches(':focus-visible')) return;
    amp = 0.4;
    activate(rowOf(it));
    pointAt(it);
    queue();
  });
  // Not during a tap: tapping another colour moves focus from the old radio to the new one.
  document.addEventListener('focusout', function (e) { if (cur && !tapEl && itemOf(e.target)) release(false); });
})();
